import { FC, useEffect } from 'react';
import { Navigate } from 'react-router';

import { useProStudioRoutes } from '../hooks';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { tableStore } from '../stores/table-store';
import type { WorkflowStage } from '../stores/utils/workflow-stage';

interface StageTarget {
  stage: WorkflowStage;
  /** Open Build on this tab instead of its default. */
  buildViewMode?: 'files';
  /** Arrive with Build's Actions panel open. */
  openActions?: boolean;
}

export const RedirectIntoStageNav: FC<StageTarget> = ({
  stage,
  buildViewMode,
  openActions,
}) => {
  const { workflows } = useProStudioRoutes();

  useEffect(() => {
    tableStore.setActiveStage(stage);
    if (buildViewMode) tableStore.setPendingBuildViewMode(buildViewMode);
    if (openActions) tableStore.setActionsPanelOpen(true);
  }, [stage, buildViewMode, openActions]);

  if (!workflows.path) return null;
  return <Navigate to={workflows.path} replace />;
};

/**
 * With `bobcat-five-stage-nav` on, a standalone solution page whose content
 * now lives in a stage redirects into that stage; off, the page renders
 * exactly as before.
 */
export const withStageNavRedirect =
  <P extends object>(Page: FC<P>, target: StageTarget): FC<P> =>
  // eslint-disable-next-line react/display-name
  (props: P) => {
    const access = useBobcatFlag('fiveStageNav');
    if (access === 'denied') return <Page {...props} />;
    if (access === 'pending') return null;
    return <RedirectIntoStageNav {...target} />;
  };
